import App from 'next/app';
import React from 'react';
import getConfig from 'next/config';
import * as Sentry from '@sentry/browser';
import { Context as ResponsiveContext } from 'react-responsive';
import * as isomorphicCookie from 'isomorphic-cookie';
import * as dateFormat from 'dateformat';
import { AxiosInstance as axios } from 'axios';
import { recordUserSession } from '../helpers/logrocket';
import SentryBoundary from '../components_common/SentryBoundary/SentryBoundary';
import timerQueue from '../helpers/TimerQueue';
import { AppContext } from '../store/appContext';

import '../components_common/customized_modules/react-photoswipe/photoswipe.css';
import '../common/css_global/mobile/styles.css';
import '../common/css_global/desktop/styles.css';
import '../common/css_global/SizeGuideModal.css';
import '../components/pages/ProductDetails/view/EyewearWarning/BubbleDisplayer.css';
import '../components/Authentication/AuthModal/styled/AuthModal.css';

import 'rc-slider/assets/index.css';
import config from '../components/MediaQuery/config';
import * as cartHelper from '../shared/helpers/cart';
import { requiresCookieBanner } from '../helpers/helpers';
import {
  aboutUs,
  ourPromise,
  press,
  termsAndCondition,
  privacy,
  reviews,
  charitableGiving,
  contactUs,
  italistLegit,
  howToShop,
  ordersAndShipping,
  returnsPolicy,
  faq,
  magazine,
  italistByYou,
} from '../helpers/routes';

const buildFooterData = () => {
  const groupOne = {
    title: 'italist marketplace',
    code: 'italist_marketplace_section',
    links: [
      {
        title: 'About us',
        url: aboutUs,
      },
      {
        title: 'Our promise',
        url: ourPromise,
      },
      {
        title: 'Press',
        url: press,
      },
      {
        title: 'Terms and conditions',
        url: termsAndCondition,
      },
      {
        title: 'Privacy policy',
        url: privacy,
      },
      {
        title: 'Reviews',
        url: reviews,
      },
      {
        title: 'Charitable giving',
        url: charitableGiving,
      },
    ],
  };
  const groupTwo = {
    title: 'Customer service',
    code: 'cc_section',
    links: [
      {
        title: 'Contact us',
        url: contactUs,
      },
      {
        title: 'Is italist legitimate?',
        url: italistLegit,
      },
      {
        title: 'How to shop',
        url: howToShop,
      },
      {
        title: 'Orders and shipping',
        url: ordersAndShipping,
      },
      {
        title: 'Returns',
        url: returnsPolicy,
      },
      {
        title: 'FAQ',
        url: faq,
      },
    ],
  };
  const groupThree = {
    title: 'Connect',
    code: 'magazine_section',
    links: [
      {
        title: 'italist magazine',
        url: magazine,
      },
      {
        title: '#italistbyyou',
        url: italistByYou,
      },
    ],
  };
  return [groupOne, groupTwo, groupThree];
};

const linkshareSiteIdCheck = (query, req, res) => {
  const reqSideId = query && query.siteID;
  const cookieSiteId = isomorphicCookie.load('ranSiteID', req);
  if (reqSideId !== undefined && reqSideId !== cookieSiteId) {
    const expireDate = new Date();
    expireDate.setFullYear(expireDate.getFullYear() + 2);
    isomorphicCookie.save(
      'ranSiteID',
      reqSideId,
      {
        domain: process.env.COOKIE_DOMAIN,
        secure: process.env.COOKIE_SECURE,
        expires: expireDate,
      },
      res,
    );

    const date = dateFormat(Date.now(), 'yyyymmdd_HHMM', true);
    isomorphicCookie.save(
      'ranLandDateTime',
      date,
      {
        domain: process.env.COOKIE_DOMAIN,
        secure: process.env.COOKIE_SECURE,
        expires: expireDate,
      },
      res,
    );
  }
};

const buildAppState = async req => {
  const quickauth = await import('../server/lib/quickauth');
  const country = (await import('../server/helpers/country')).default(quickauth);
  const s3 = await import('../server/helpers/s3');
  const cloudImage = await import('../server/helpers/cloudImage');
  const menu = (await import('../server/helpers/menu')).default(cloudImage);
  const { path, hostname, originalUrl, query } = req;
  let session = {};
  if (req.session !== undefined) {
    session = req.session;
  }

  const cartItems = cartHelper.getCachedCartCounter(session);
  if (cartItems === undefined) {
    const cart = await cartHelper.getCart(session.userid, req.sessionID, session.mkt_country_code);
    if (cart !== null) {
      cartHelper.cacheCartCounterFromCartItems(session, cart);
    }
  }

  const isAdmin = quickauth.hasGroup(session, 'administrators');
  const hasAdminArea =
    isAdmin ||
    quickauth.hasGroup(session, 'negozi') ||
    quickauth.hasGroup(session, 'team') ||
    quickauth.hasGroup(session, 'agents') ||
    quickauth.hasGroup(session, 'mkt') ||
    quickauth.hasGroup(session, 'master') ||
    quickauth.hasGroup(session, 'operations') ||
    quickauth.hasGroup(session, 'customer care') ||
    quickauth.hasGroup(session, 'cdg/amm');

  return {
    auth: quickauth.isLoggedIn(session),
    paths: {
      web: process.env.BASE_PATH_WEB,
      admin: process.env.BASE_PATH_ADMIN,
      secure: process.env.BASE_PATH_SECURE,
      account: process.env.BASE_PATH_ACCOUNT,
    },
    country: session.mkt_country,
    countryId: session.mkt_country_id,
    countries: country.getActiveCountriesPretty(),
    madeInCountries: country.getMadeInCountries(),
    countriesForSeo: country.getCountriesForSeoPretty(),
    countryIso: session.mkt_country_code,
    countryCode: session.mkt_country_code,
    currencySymbol: session.currency_symbol,
    billingCurrencyCode: session.currency_code,
    approxCurrencyCode: session.approx_currency_code,
    devicePixelRatio: session.device_pixel_ratio,
    euCookiePolicyAccepted: session.eu_cookie_policy_accepted,
    numCartItems: cartItems,
    s3BasePath: s3.getS3BasePath(),
    userId: quickauth.getUserID(session),
    fbOptions: {
      debug: process.env.ENVIRONMENT !== 'production',
    },
    email: session.email,
    footerData: buildFooterData(),
    location: {
      path,
      hostname,
      originalUrl,
      query,
    },
    menu: menu.getMenu(),
    department: session.department || 'women',
    recaptchaKey: process.env.RECAPTCHA_PUBLIC_KEY,
    isEu: session.user_in_europe,
    isAdmin,
    hasAdminArea,
    scripts: {
      riskifiedDomain: process.env.RISKIFIED_DOMAIN,
      riskifiedSessionId: session.riskified_session_id,
    },
    userAgent: req.useragent,
    freeShipping: true,
    runAdroll: !requiresCookieBanner(session.user_in_europe, session.eu_cookie_policy_accepted),
  };
};

class MyApp extends App {
  constructor(props) {
    super(props);
    const dsn = process.env.SENTRY_DSN_CONSUMER || null;
    const { publicRuntimeConfig } = getConfig();
    if (dsn && publicRuntimeConfig.production) {
      Sentry.init({
        dsn,
        environment: 'production',
        release: publicRuntimeConfig.gitHash,
        attachStacktrace: true,
      });
    }
    this.state = {
      isMountedOnBrowser: false,
    };
  }

  componentDidMount() {
    timerQueue.init();
    // setTimeout(() => axios.post('/api/store_client_info', getClientInfo()), 0);
    const {
      serverRuntimeConfig: { isServer },
    } = getConfig();
    if (!isServer) {
      this.setState({
        isMountedOnBrowser: true,
      });
    }
  }

  static async getInitialProps({ Component, ctx }) {
    const { req, res } = ctx;
    if (!req || req.path.startsWith('_next/') || req.path.startsWith('static/')) {
      return { pageProps: {} };
    }
    req.datadog.increment('consumer_app.app_js');
    linkshareSiteIdCheck(req.query, req, res);
    let appState = await buildAppState(req);
    const pageState = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};
    appState = { ...appState, ...pageState };
    return { pageProps: appState };
  }

  render() {
    const { Component, pageProps } = this.props;
    const { userAgent, userId } = pageProps;

    // Allow browser to re-render after hydration
    const responsiveContext = this.state.isMountedOnBrowser
      ? undefined
      : { width: userAgent && userAgent.isMobile ? config.mobile.maxWidth : config.desktop.minWidth };

    if (this.state.isMountedOnBrowser) {
      recordUserSession(userId);
    }
    return (
      <ResponsiveContext.Provider value={responsiveContext}>
        <AppContext.Provider value={pageProps}>
          <SentryBoundary>
            <Component {...pageProps} />
          </SentryBoundary>
        </AppContext.Provider>
      </ResponsiveContext.Provider>
    );
  }
}

export default MyApp;
